<template>
  <v-autocomplete
    v-bind="attrsOnDefault"
    v-model="value"
    :menuProps="
      $vfm.openedModals && $vfm.openedModals.length > 0
        ? { attach: '.modal-container:last-child' }
        : undefined
    "
    :class="{
      'input-success': fieldMeta && fieldMeta.validated && fieldMeta.valid,
    }"
  >
    <template v-slot:append-inner v-if="fieldMeta && fieldMeta.validated">
      <v-icon
        v-if="!fieldMeta.valid"
        icon="$mdiInformationOutline"
        size="small"
      />
      <v-icon v-else icon="$mdiCheckBold" size="small" />
    </template>
    <template
      v-for="(slot, index) in slotNames"
      :key="index"
      v-slot:[slot]="props"
    >
      <slot :name="slot" v-bind="props"></slot>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { FieldMeta } from "vee-validate";
import { useSelectValue } from "@/composable/select-value";

/**
 * v-autocompleteのラッパーコンポーネント
 * VeeValidateのチェック結果をアイコンとして表示しているので
 */
export default defineComponent({
  name: "AppSelectSearch",
  setup() {
    const { getMatchedItem } = useSelectValue();
    return { getMatchedItem };
  },
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number, Object] as PropType<string | number | Object>,
      required: false,
    },
    fieldMeta: {
      type: Object as PropType<FieldMeta<unknown>>,
      required: false,
    },
  },
  emits: ["update:modelValue"],
  computed: {
    /**
     * slotは公式ページへ参照
     * https://vuetifyjs.com/en/api/v-autocomplete/#slots
     *
     * 注意:戻り値は"selection"[]の形式ではないとTypescriptが型エラーがある
     */
    slotNames(): "selection"[] {
      return Object.keys(this.$slots) as [];
    },
    attrsOnDefault(): { [key: string]: unknown } {
      /**
       * VeeValidateのonUpdate:modelValueで値を監視しているので、他のEventをスキップ
       * （Changeイベント二回実行して,nullを空文字に変更してしまう）
       */
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { onInput, onBlur, onChange, ...others } = this.$attrs;
      return others;
    },
    value: {
      get(): unknown {
        // WVS2UI-13557 #20 画面では選択肢に存在しない項目を指定すれば、nullを返す
        if (this.modelValue !== null && this.modelValue !== undefined) {
          if (Array.isArray(this.modelValue)) {
            // 今までmultiple選択の使い場所はProfileInternetFwEdit.vueしかない、該当場所はちゃんとクリアロジックを実装しているので、スキップ
            return this.modelValue;
          } else {
            // 単一選択の場合
            const matchedItem = this.getMatchedItem(
              this.modelValue,
              this.$attrs.items as [],
              this.$attrs["item-value"] as string | undefined,
              this.$attrs["return-object"] === true ||
                this.$attrs["return-object"] === ""
                ? true
                : false
            );
            if (matchedItem) {
              // 選択肢に存在する場合、そのまま返す
              return this.modelValue;
            } else {
              // 無効の値を指定した場合、親画面にもnullにするため
              this.$emit("update:modelValue", null);
              // v-selectにnullを渡すため
              return null;
            }
          }
        } else {
          // modelValueがnullやundefinedの場合、そのまま返す
          return this.modelValue;
        }
      },
      set(newValue: unknown) {
        this.$emit("update:modelValue", newValue);
      },
    },
  },
});
</script>
